/**
 * StudioTopBar — navigation chrome above the calendar grid.
 *
 *  [+]  [<]  Today  [>]      Day  Week  Month  Quarter  Year      [search]
 *
 * Day / Week are functional; Month / Quarter / Year are present for
 * visual parity with the inspiration but render the same week view for now.
 */

import { ChevronLeft, ChevronRight, Plus, Search } from 'lucide-react'
import { useStudioStore } from '@/stores/useStudioStore'
import { type CalendarView } from '@/features/studio/lib/time'
import { format } from 'date-fns'

const VIEWS: CalendarView[] = ['day', 'week', 'month', 'quarter', 'year']

export function StudioTopBar() {
  const calendarView = useStudioStore((s) => s.calendarView)
  const calendarAnchor = useStudioStore((s) => s.calendarAnchor)
  const setCalendarView = useStudioStore((s) => s.setCalendarView)
  const shiftCalendar = useStudioStore((s) => s.shiftCalendar)
  const goToToday = useStudioStore((s) => s.goToToday)

  const anchor = new Date(calendarAnchor)

  return (
    <div className="flex items-center gap-4 px-6 h-14 border-b border-neutral-800 bg-neutral-900 flex-shrink-0">
      {/* Left: create + nav */}
      <button
        type="button"
        className="w-8 h-8 rounded-full border border-neutral-700 hover:border-neutral-500 flex items-center justify-center text-neutral-400 hover:text-neutral-100 transition"
        title="Create block (coming soon)"
      >
        <Plus className="w-4 h-4" strokeWidth={1.5} />
      </button>

      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => shiftCalendar(-1)}
          className="w-8 h-8 rounded hover:bg-neutral-800 flex items-center justify-center text-neutral-400 hover:text-neutral-100 transition"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={goToToday}
          className="px-3 h-8 text-sm font-light text-neutral-200 rounded hover:bg-neutral-800 transition"
        >
          Today
        </button>
        <button
          type="button"
          onClick={() => shiftCalendar(1)}
          className="w-8 h-8 rounded hover:bg-neutral-800 flex items-center justify-center text-neutral-400 hover:text-neutral-100 transition"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Anchor date label */}
      <div className="text-sm text-neutral-400 font-light min-w-[120px]">
        {format(anchor, 'MMMM yyyy')}
      </div>

      {/* View toggles */}
      <div className="flex items-center gap-1 ml-auto bg-neutral-800 rounded-md p-1">
        {VIEWS.map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setCalendarView(v)}
            className={
              'px-3 h-7 text-xs font-light uppercase tracking-wide rounded transition ' +
              (calendarView === v
                ? 'bg-neutral-700 text-neutral-100'
                : 'text-neutral-400 hover:text-neutral-200')
            }
          >
            {v}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative w-64">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
        <input
          type="text"
          placeholder="Search"
          className="w-full h-9 pl-10 pr-3 bg-neutral-800 border border-neutral-700 rounded-md text-sm text-neutral-100 placeholder:text-neutral-500 focus:outline-none focus:border-neutral-500"
          disabled
        />
      </div>
    </div>
  )
}
